import type { ReactElement } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { isEmpty, noop } from "lodash";

import { Dropdown, EmptyState, IconButton, QueryBarGroupItem } from "@similarweb/ui-components";

import useActivationQueryWithAccessControl from "app/custom-hooks/activation/useActivationQueryWithAccessControl";
import { translate } from "app/filters/i18nFilter";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import { ActivionSetItem } from "./ActivionSetItem";
import {
    ACTIVATION_SETS_QUERY_BAR_ITEM_ICON,
    ACTIVATION_SETS_QUERY_BAR_ITEM_SECONDARY_TEXT,
    DROPDOWN_BUTTON_ICON,
    DROPDOWN_BUTTON_KEY,
    DROPDOWN_BUTTON_TEXT_KEY,
    DROPDOWN_ERROR_SUBTITLE,
    DROPDOWN_ERROR_TITLE,
    DROPDOWN_ITEM_NO_SETS_CREATED,
    DROPDOWN_PLACEMENT,
    DROPDOWN_SEARCH_PLACEHOLDER,
} from "./const";
import { CreateActivationSetCTA } from "./CreateActivationSetCTA";
import * as SC from "./styles";

export const ActivationSetsQueryBarFilter = () => {
    const { data: activationSets, isLoading, isFetching, isError } = useActivationQueryWithAccessControl();
    const isSetsLoading = isLoading || isFetching;
    const { activationSetId } = useCurrentStateAndParams().params;
    const currentActiveSet = activationSets?.find((set) => set.id === activationSetId);

    const searchFilter = (term: string, item: ReactElement<{ text: string }>): boolean =>
        item.props.text.toLowerCase().startsWith(term.toLowerCase());

    const activationSetsItems = useMemo(() => {
        if (isError) {
            return [
                <EmptyState
                    key="acitvation-sets-filter-error"
                    title={translate(DROPDOWN_ERROR_TITLE)}
                    subtitle={translate(DROPDOWN_ERROR_SUBTITLE)}
                />,
            ];
        }
        if (!isSetsLoading && !activationSets.length) {
            return [
                <SC.NoSetsCreated key="no-sets-created">{translate(DROPDOWN_ITEM_NO_SETS_CREATED)}</SC.NoSetsCreated>,
            ];
        }
        const userSets = activationSets.filter((set) => !set.sharedBy);
        const sharedSets = activationSets.filter((set) => set.sharedBy);
        return [...userSets, ...sharedSets]
            .filter((set) => set && !(typeof set === "object" && isEmpty(set)))
            .map((set) => {
                const { id, name, allDomains, sharedBy } = set;
                return (
                    <ActivionSetItem
                        key={id}
                        id={id}
                        text={name}
                        allDomains={allDomains}
                        sharedBy={sharedBy}
                    />
                );
            });
    }, [activationSets, isSetsLoading, isError]);

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            activationTracking.queryBarFilter.trackToggleFilter();
        }
    };

    const footerWrapped = () =>
        isSetsLoading ? null : <CreateActivationSetCTA currentSetsCount={activationSets?.length} />;

    const updateUrlWithActivationSet = (id: string) => {
        let updatedParams: { activationSetId: string; country?: number } = { activationSetId: id };
        if (id) {
            const selectedSet = activationSets?.find((set) => set.id === id);
            updatedParams = { ...updatedParams, country: selectedSet?.country };
        }
        proNavigator.updateParams(updatedParams, { reload: false });
    };

    const onItemClick = ({ id }: { id: string }) => {
        activationTracking.queryBarFilter.trackFilterItemClick(id);
        updateUrlWithActivationSet(id);
    };

    return (
        <Dropdown
            onClick={onItemClick}
            onToggle={onToggle}
            dropdownPopupPlacement={DROPDOWN_PLACEMENT}
            buttonWidth={"auto"}
            footerComponent={footerWrapped}
            width={348}
            hasSearch={true}
            searchFilter={searchFilter}
            searchPlaceHolder={currentActiveSet?.name ?? translate(DROPDOWN_SEARCH_PLACEHOLDER)}
        >
            {[
                activationSetId ? (
                    <QueryBarGroupItem
                        text={currentActiveSet?.name}
                        icon={ACTIVATION_SETS_QUERY_BAR_ITEM_ICON}
                        activeIcon={ACTIVATION_SETS_QUERY_BAR_ITEM_ICON}
                        secondaryText={translate(ACTIVATION_SETS_QUERY_BAR_ITEM_SECONDARY_TEXT)}
                        onButtonClick={(e) => {
                            e.stopPropagation();
                            updateUrlWithActivationSet(undefined);
                        }}
                        onItemClick={noop}
                        isCompare={true}
                    />
                ) : (
                    <IconButton
                        key={DROPDOWN_BUTTON_KEY}
                        iconName={DROPDOWN_BUTTON_ICON}
                        type={buttonTypes.OUTLINED}
                        onClick={noop}
                        isDisabled={false}
                    >
                        {translate(DROPDOWN_BUTTON_TEXT_KEY)}
                    </IconButton>
                ),
                ...activationSetsItems,
            ]}
        </Dropdown>
    );
};
